import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowsClockwise, GearSix, Sparkle, Printer, Tray, GraduationCap, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  fetchProfile,
  refreshProfile as apiRefresh,
  fetchConfig,
  updateConfig as apiUpdateConfig,
  fetchInsights,
  regenerateInsights as apiRegenInsights,
  fetchCredentials,
  listGoals,
  createGoal,
  deleteGoal,
} from "@/lib/api";
import ProfileHeader from "@/components/scholar/ProfileHeader";
import MetricsCards from "@/components/scholar/MetricsCards";
import CitationsChart from "@/components/scholar/CitationsChart";
import PublicationsTable from "@/components/scholar/PublicationsTable";
import PublicationDetailDialog from "@/components/scholar/PublicationDetailDialog";
import Credentials from "@/components/scholar/Credentials";
import ScopusProfiles from "@/components/scholar/ScopusProfiles";
import FeaturedBooks from "@/components/scholar/FeaturedBooks";
import AIInsights from "@/components/scholar/AIInsights";
import Biography from "@/components/scholar/Biography";
import SettingsDialog from "@/components/scholar/SettingsDialog";
import LoadingState from "@/components/scholar/LoadingState";
import Goals from "@/components/scholar/Goals";
import GoalDialog from "@/components/scholar/GoalDialog";
import ServicesSection from "@/components/scholar/ServicesSection";
import InquiriesDialog from "@/components/scholar/InquiriesDialog";

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

export default function Dashboard() {
  const [profile, setProfile] = useState(null);
  const [config, setConfig] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [inquiriesOpen, setInquiriesOpen] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);

  const reloadGoals = useCallback(async () => {
    try {
      const g = await listGoals();
      setGoals(g);
    } catch (e) {
      console.error("Failed to reload goals", e);
    }
  }, []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [p, c, cr, g] = await Promise.all([
        fetchProfile(),
        fetchConfig(),
        fetchCredentials().catch(() => null),
        listGoals().catch(() => []),
      ]);
      setProfile(p);
      setConfig(c);
      setCredentials(cr);
      setGoals(g || []);
    } catch (e) {
      toast.error("Could not load Scholar data", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const data = await apiRefresh();
      setProfile(data);
      const c = await fetchConfig();
      setConfig(c);
      await reloadGoals();
      toast.success("Scholar profile re-synced");
    } catch (e) {
      toast.error("Refresh failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setRefreshing(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      setInsightsLoading(true);
      const data = await apiRegenInsights();
      setInsights(data);
      toast.success("AI insights regenerated");
    } catch (e) {
      toast.error("Could not regenerate insights", { description: e?.response?.data?.detail || e.message });
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleSaveConfig = async (user_id) => {
    try {
      const c = await apiUpdateConfig(user_id);
      setConfig(c);
      setSettingsOpen(false);
      toast.success("Scholar URL updated", { description: "Re-syncing profile..." });
      await handleRefresh();
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || e.message });
    }
  };

  const handleAddGoal = async (payload) => {
    try {
      await createGoal(payload);
      toast.success("Milestone added");
      setGoalOpen(false);
      await reloadGoals();
    } catch (e) {
      toast.error("Could not add milestone", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  };

  const handleDeleteGoal = async (id) => {
    try {
      await deleteGoal(id);
      toast.success("Milestone removed");
      await reloadGoals();
    } catch (e) {
      toast.error("Could not remove milestone", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  };

  const handleExportPdf = () => {
    toast.info("Opening print dialog", {
      description: "Choose 'Save as PDF' to export the editorial report.",
    });
    setTimeout(() => window.print(), 250);
  };

  if (loading || !profile) return <LoadingState />;

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header
        className="sticky top-0 z-30 backdrop-blur-md bg-paper/85 border-b border-ink/10 no-print"
        data-testid="dashboard-header"
      >
        <div className="h-1.5 w-full flex">
          <span className="flex-1 bg-coral" />
          <span className="flex-1 bg-saffron" />
          <span className="flex-1 bg-mint" />
          <span className="flex-1 bg-teal" />
          <span className="flex-1 bg-lilac" />
          <span className="flex-1 bg-linkedin" />
          <span className="flex-1 bg-terracotta" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-baseline gap-3">
            <span className="font-display italic text-2xl text-terracotta leading-none">Scholar</span>
            <span className="font-chunk text-2xl tracking-tight uppercase">Atlas</span>
            <span className="hidden md:inline overline ml-4">An academic performance ledger</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleExportPdf}
              className="btn-pill btn-pill-ink"
              data-testid="export-pdf-btn"
            >
              <Printer size={14} weight="bold" />
              Export PDF
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="btn-pill disabled:opacity-50"
              data-testid="refresh-data-btn"
            >
              <ArrowsClockwise size={14} weight="bold" className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Syncing" : "Re-sync"}
            </button>
            <button
              type="button"
              onClick={() => setInquiriesOpen(true)}
              className="btn-pill btn-pill-pink"
              data-testid="open-inquiries-btn"
            >
              <Tray size={14} weight="bold" />
              Inbox
            </button>
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="btn-pill"
              data-testid="open-settings-btn"
            >
              <GearSix size={14} weight="bold" />
              Settings
            </button>
          </div>
        </div>
      </header>

      <div className="hidden print:block px-12 pt-2 pb-6 border-b border-ink">
        <div className="overline mb-2">Scholar Atlas · Academic Performance Report</div>
        <div className="font-display text-4xl tracking-tight">{credentials?.display_name || profile.name}</div>
        <div className="text-sm text-ink-mute mt-1">
          {profile.affiliation} · Generated {new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
        </div>
      </div>

      <main
        className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-14 space-y-12 lg:space-y-16"
        data-print-region="dashboard"
      >
        <motion.section {...fadeUp}>
          <ProfileHeader
            profile={profile}
            lastSyncedAt={config?.last_synced_at}
            externalProfiles={credentials?.external_profiles || []}
            customPhotoUrl={credentials?.custom_photo_url}
            displayName={credentials?.display_name}
          />
        </motion.section>

        {profile.interests?.length > 0 && (
          <motion.section
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.03 }}
            className="marquee-wrap no-print -mx-6 lg:-mx-12"
            data-testid="interests-marquee"
            aria-hidden="true"
          >
            <div className="marquee-track">
              {[...Array(2)].map((_, dup) => (
                <div key={dup} className="flex items-center gap-8 px-4">
                  {profile.interests.map((it, idx) => (
                    <span key={`${dup}-${idx}`} className="marquee-item">
                      <span className="marquee-star">✦</span>
                      <span>{it.label}</span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {credentials?.featured_books?.length > 0 && (
          <motion.section {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }} className="relative">
            <div aria-hidden className="absolute -top-6 -right-4 w-24 h-24 bg-coral/20 rounded-full blur-2xl pointer-events-none" />
            <SectionTitle
              eyebrow="00 — In print"
              title="Two Scopus-indexed volumes from IGI Global"
              accent="coral"
            />
            <FeaturedBooks books={credentials.featured_books} />
          </motion.section>
        )}

        <motion.section
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.045 }}
          className="relative"
          data-testid="appointment-highlight"
        >
          <div aria-hidden className="absolute -top-5 right-6 w-28 h-28 bg-lime/30 rounded-full blur-3xl pointer-events-none" />
          <div className="sticker sticker-tilt-l bg-saffron/25 p-6 sm:p-8 lg:p-9 relative overflow-hidden">
            {/* top color ribbon */}
            <div className="absolute top-0 left-0 right-0 h-3 flex">
              <span className="flex-1 bg-coral" />
              <span className="flex-1 bg-saffron" />
              <span className="flex-1 bg-mint" />
              <span className="flex-1 bg-teal" />
              <span className="flex-1 bg-lilac" />
            </div>
            {/* decorative sparkles */}
            <Star size={22} weight="fill" className="absolute top-6 right-8 text-coral animate-pulse" />
            <Sparkle size={16} weight="fill" className="absolute bottom-8 right-16 text-teal" />

            <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7 mt-3">
              <div className="shrink-0 self-start">
                <span className="relative inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-ink text-lime border-2 border-ink shadow-[5px_5px_0_0_#E8A33D] -rotate-3">
                  <GraduationCap size={40} weight="duotone" />
                </span>
              </div>
              <div className="min-w-0">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-hotpink text-white border-2 border-ink text-[10px] font-mono uppercase tracking-widest shadow-[3px_3px_0_0_#1A1A1A]">
                  <Star size={11} weight="fill" />
                  New Appointment
                </span>
                <h2 className="font-chunk text-2xl sm:text-3xl lg:text-4xl tracking-tight uppercase leading-[1.15] mt-4">
                  Appointed as <span className="hl-coral">PhD Guide</span> for{" "}
                  <span className="hl-lime">Golden Gate University, California</span>
                </h2>
                <p className="font-hand text-2xl sm:text-3xl text-terracotta leading-snug mt-3">
                  Now mentoring doctoral scholars on a global stage.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="relative">
          <div aria-hidden className="absolute -top-8 left-4 w-32 h-32 bg-saffron/25 rounded-full blur-3xl pointer-events-none" />
          <SectionTitle eyebrow="01 — Metrics" title="Citation impact at a glance" accent="terracotta" />
          <MetricsCards metrics={profile.metrics} />
        </motion.section>

        <motion.section {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="relative">
          <div aria-hidden className="absolute -top-4 -right-8 w-28 h-28 bg-mint/25 rounded-full blur-3xl pointer-events-none" />
          <SectionTitle eyebrow="02 — Trajectory" title="Citations per year" accent="saffron" />
          <CitationsChart data={profile.citations_per_year || []} />
        </motion.section>

        {credentials?.scopus_profiles?.length > 0 && (
          <motion.section {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.13 }} className="relative">
            <div aria-hidden className="absolute -top-6 right-12 w-24 h-24 bg-lilac/30 rounded-full blur-3xl pointer-events-none" />
            <SectionTitle
              eyebrow="03 — Scopus"
              title="Indexed identities & document counts"
              accent="lilac"
            />
            <ScopusProfiles profiles={credentials.scopus_profiles} />
          </motion.section>
        )}

        {credentials?.services?.length > 0 && (
          <motion.section {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.14 }} className="relative">
            <div aria-hidden className="absolute -top-6 left-8 w-28 h-28 bg-coral/25 rounded-full blur-3xl pointer-events-none" />
            <SectionTitle
              eyebrow="04 — Services"
              title="Research mentorship & guidance services"
              accent="coral"
            />
            <ServicesSection
              services={credentials.services}
              contactEmail={credentials.contact_email}
            />
          </motion.section>
        )}

        <motion.section {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="relative">
          <div aria-hidden className="absolute -top-4 left-12 w-28 h-28 bg-teal/25 rounded-full blur-3xl pointer-events-none" />
          <SectionTitle
            eyebrow="05 — Profile"
            title="Academic biography"
            accent="teal"
          />
          {credentials?.biography ? (
            <Biography biography={credentials.biography} />
          ) : (
            <AIInsights insights={insights} loading={insightsLoading} />
          )}
        </motion.section>

        <motion.section {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="relative">
          <div aria-hidden className="absolute -top-4 right-4 w-24 h-24 bg-saffron/30 rounded-full blur-3xl pointer-events-none" />
          <SectionTitle
            eyebrow="06 — Milestones"
            title="Goals & academic targets"
            accent="saffron"
          />
          <Goals goals={goals} onAdd={() => setGoalOpen(true)} onDelete={handleDeleteGoal} />
        </motion.section>

        <motion.section {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.25 }} className="relative">
          <div aria-hidden className="absolute -top-6 -left-2 w-28 h-28 bg-indigo/25 rounded-full blur-3xl pointer-events-none" />
          <SectionTitle
            eyebrow="07 — Publications"
            title={`${profile.publications?.length || 0} works in the record`}
            accent="indigo"
          />
          <p className="text-sm text-ink-mute -mt-4 mb-4 no-print">
            Tap a row for the abstract and per-year citation breakdown.
          </p>
          <PublicationsTable
            publications={profile.publications || []}
            onSelect={(p) => setSelectedPaper(p)}
          />
        </motion.section>

        <motion.section {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="relative">
          <div aria-hidden className="absolute -top-4 right-16 w-28 h-28 bg-coral/25 rounded-full blur-3xl pointer-events-none" />
          <SectionTitle
            eyebrow="08 — Credentials"
            title="Certificates, editorships & academic service"
            accent="coral"
          />
          <Credentials data={credentials} />
        </motion.section>

        <footer className="pt-10 border-t border-ink/10 flex items-center justify-between text-xs font-mono text-ink-mute flex-wrap gap-2">
          <span>Source · Google Scholar profile {profile.user_id}</span>
          <a
            href={profile.profile_url}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-terracotta transition-colors"
            data-testid="external-profile-link"
          >
            View on Scholar →
          </a>
        </footer>
      </main>

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        currentUserId={config?.user_id}
        onSave={handleSaveConfig}
      />
      <GoalDialog open={goalOpen} onOpenChange={setGoalOpen} onSave={handleAddGoal} />
      <InquiriesDialog open={inquiriesOpen} onOpenChange={setInquiriesOpen} />
      <PublicationDetailDialog
        open={!!selectedPaper}
        onOpenChange={(v) => {
          if (!v) setSelectedPaper(null);
        }}
        paperId={selectedPaper?.paper_id}
        fallback={selectedPaper}
      />
    </div>
  );
}

const ACCENT_COLOR = {
  terracotta: { eyebrow: "text-terracotta", badge: "bg-terracotta", underline: "text-terracotta/30", hl: "hl-terracotta" },
  saffron: { eyebrow: "text-saffron-hover", badge: "bg-saffron text-ink", underline: "text-saffron/45", hl: "hl-saffron" },
  teal: { eyebrow: "text-teal", badge: "bg-teal", underline: "text-teal/30", hl: "hl-mint" },
  indigo: { eyebrow: "text-indigo", badge: "bg-indigo", underline: "text-indigo/30", hl: "hl-lilac" },
  coral: { eyebrow: "text-coral-hover", badge: "bg-coral", underline: "text-coral/35", hl: "hl-coral" },
  mint: { eyebrow: "text-mint-hover", badge: "bg-mint", underline: "text-mint/40", hl: "hl-mint" },
  lilac: { eyebrow: "text-lilac-hover", badge: "bg-lilac", underline: "text-lilac/35", hl: "hl-lilac" },
};

function SectionTitle({ eyebrow, title, inline = false, accent = "terracotta" }) {
  const tag = eyebrow.split(" ")[0];
  const palette = ACCENT_COLOR[accent] || ACCENT_COLOR.terracotta;
  const [num, ...rest] = eyebrow.split(" — ");
  const label = rest.join(" — ");
  // Highlight the last word of the title in a solid color block, GenZ-zine style.
  const words = title.split(" ");
  const lastWord = words.pop();
  const leading = words.join(" ");
  return (
    <div className={inline ? "" : "mb-6 lg:mb-8"} data-testid={`section-${tag}`}>
      <div className="flex items-center gap-2.5">
        <span className={`section-number ${palette.badge}`}>{num}</span>
        <span className={`overline ${palette.eyebrow}`}>{label}</span>
      </div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-2.5 leading-tight">
        {leading && <span>{leading} </span>}
        <span className={palette.hl}>{lastWord}</span>
      </h2>
    </div>
  );
}
